import { useState, useMemo } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  MessageSquare,
  FileText,
  Search,
  ListChecks,
  Sparkles,
  BookOpen,
  FileSearch,
  Languages,
  Table,
  Mail,
  ChevronRight,
  ClipboardList,
  FileOutput,
  HelpCircle,
  X,
  Clock,
  CalendarClock,
  Crown,
  PenLine,
  Gavel,
  Network,
  ScanSearch,
  Wrench,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AVAILABLE_TASKS, TASK_TO_CATEGORY } from '@/types/taskTypes';

interface QuickStartScreenProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartChat?: () => void;
  onOpenDocumentModal?: () => void;
  onOpenUploadModal?: () => void;
  onNavigateToPage?: (page: 'uspto_data' | 'resources' | 'examination' | 'tasks' | 'ids' | 'forms' | 'templates', applicationNumber?: string, taskId?: string) => void;
  onOpenWizard?: () => void;
}

interface QuickStartOption {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  category: 'tools' | 'chat' | 'document' | 'task' | 'research' | 'ids' | 'forms';
  action: () => void;
  gradient: string;
  keywords: string[];
  hint: string;
  comingSoon?: boolean;
  premiumOnly?: boolean;
}

const CATEGORY_GRADIENTS: Record<string, string> = {
  legacy: 'from-slate-500 to-gray-500',
  invention_record: 'from-blue-500 to-cyan-500',
  draft_processing: 'from-green-500 to-emerald-500',
  form_processing: 'from-indigo-500 to-purple-500',
  office_action: 'from-red-500 to-pink-500',
  translation: 'from-violet-500 to-purple-500',
};

const STOP_WORDS = new Set(['a', 'an', 'the', 'and', 'or', 'for', 'to', 'of', 'in', 'on', 'with', 'from', 'by', 'at', 'is', 'are', 'be', 'was', 'its']);

function generateKeywords(name: string, description: string): string[] {
  const words = `${name} ${description}`.toLowerCase().split(/[\s(),/]+/)
    .filter(w => w.length > 2 && !STOP_WORDS.has(w));
  return [...new Set(words)];
}

function scoreMatch(query: string, option: QuickStartOption): number {
  const q = query.toLowerCase();
  const words = q.split(/\s+/).filter(w => w.length > 1);
  if (words.length === 0) return 0;

  let score = 0;
  const titleLower = option.title.toLowerCase();
  const descLower = option.description.toLowerCase();

  // Title exact substring match is strongest
  if (titleLower.includes(q)) score += 20;

  for (const word of words) {
    if (titleLower.includes(word)) score += 10;
    if (descLower.includes(word)) score += 5;
    for (const kw of option.keywords) {
      if (kw.includes(word)) score += 7;
      // Partial match for longer keywords
      if (word.length >= 3 && kw.startsWith(word)) score += 4;
    }
  }

  return score;
}

export function QuickStartScreen({
  open,
  onOpenChange,
  onStartChat,
  onOpenDocumentModal,
  onOpenUploadModal,
  onNavigateToPage,
  onOpenWizard
}: QuickStartScreenProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const categories = [
    { id: 'tools', label: 'Tools', icon: Wrench },
    { id: 'chat', label: 'Chat & Analyze', icon: MessageSquare },
    { id: 'document', label: 'Documents', icon: FileText },
    { id: 'task', label: 'Tasks', icon: ListChecks },
    { id: 'ids', label: 'IDS', icon: ClipboardList },
    { id: 'forms', label: 'Forms', icon: FileText },
    { id: 'research', label: 'Research', icon: Search },
  ];

  const quickStartOptions: QuickStartOption[] = [
    // Drafting
    {
      id: 'drafting',
      title: 'Draft an Application',
      description: 'Build a patent application from a disclosure, one confirmed step at a time',
      icon: PenLine,
      category: 'tools',
      gradient: 'from-violet-500 to-indigo-500',
      keywords: [
        'draft', 'drafting', 'draft application', 'write application', 'new application',
        'patent application', 'specification', 'spec', 'detailed description',
        'claims', 'draft claims', 'background', 'summary', 'abstract',
        'figures', 'drawings', 'reference numerals', 'numerals', 'parts list',
        'disclosure', 'invention disclosure', 'prepare application'
      ],
      hint: 'Start from raw notes, an uploaded disclosure, or a patent in your library. Contributions, background, claims, figures, and the detailed description are built and confirmed step by step.',
      action: () => {
        navigate('/drafting');
        onOpenChange(false);
      }
    },
    // Answering an Office action, end to end
    {
      id: 'action',
      title: 'Answer an Office Action',
      description: 'A rejection to the response that gets filed — rejections, strategy, amendments, remarks',
      icon: Gavel,
      category: 'tools',
      gradient: 'from-rose-500 to-red-500',
      keywords: [
        'office action', 'oa', 'response', 'respond', 'answer', 'rejection',
        'rejections', '102', '103', '112', '101', 'amendment', 'amendments',
        'amend claims', 'remarks', 'arguments', 'argue', 'traverse',
        'claim listing', 'status identifier', 'shell', 'matter', 'prosecution',
        'final rejection', 'non-final', 'examiner'
      ],
      hint: 'One matter per application. Read the action, settle a strategy per rejection, write the amendments and the remarks, and take out a response with the claim listing built to 37 CFR 1.121(c).',
      action: () => {
        navigate('/action');
        onOpenChange(false);
      }
    },
    // Reading a whole technical field
    {
      id: 'fortech',
      title: 'Research a Technical Field',
      description: 'Build a corpus of documents and read them together — concepts, problems, solutions, novelty',
      icon: Network,
      category: 'tools',
      gradient: 'from-sky-500 to-blue-600',
      keywords: [
        'fortech', 'prior art', 'landscape', 'field', 'domain', 'corpus',
        'novelty', 'inventive step', 'obviousness', 'search', 'state of the art',
        'concepts', 'glossary', 'terminology', 'competitors', 'problem solution',
        'feature map', 'freedom to operate', 'research'
      ],
      hint: 'Gather the documents that matter in a field, and every concept, problem and solution is extracted and tied back to the passage it came from. Then work novelty and inventive step against the set.',
      action: () => {
        navigate('/fortech');
        onOpenChange(false);
      }
    },
    // Checking an application this tool did not write
    {
      id: 'drafting-review',
      title: 'Check an Application',
      description: 'Run the drafting checks over a draft or a filing — antecedent basis, claim support, numerals',
      icon: ScanSearch,
      category: 'tools',
      gradient: 'from-emerald-500 to-teal-600',
      keywords: [
        'check', 'review', 'proofread', 'antecedent basis', 'claim support',
        'reference numerals', 'numerals', 'figures described', 'filing checklist',
        'before filing', 'quality', 'audit', 'validate', 'mpf', 'means plus function'
      ],
      hint: 'Upload a draft or a previous filing and run the same checks the drafting tool runs at its review step. Most applications worth checking were not drafted here.',
      action: () => {
        navigate('/drafting/reviews');
        onOpenChange(false);
      }
    },
    // Docket
    {
      id: 'docket',
      title: 'Open Docket',
      description: 'Track patent, trademark, and other IP matters and their deadlines',
      icon: CalendarClock,
      category: 'tools',
      gradient: 'from-amber-500 to-orange-500',
      keywords: [
        'docket', 'docketing', 'deadline', 'deadlines', 'due date', 'due dates',
        'calendar', 'matter', 'matters', 'trademark', 'tm', 'annuity',
        'maintenance fee', 'renewal', 'statement of use', 'section 8',
        'office action response due', 'reminder', 'overdue', 'diary'
      ],
      hint: 'Your docket of matters and deadlines. Each user has their own; share yours with a colleague from Docket -> Sharing.',
      action: () => {
        navigate('/docket');
        onOpenChange(false);
      }
    },
    // Chat options
    {
      id: 'chat-general',
      title: 'Start a Chat',
      description: 'Ask questions, analyze patents, or get help with patent tasks',
      icon: MessageSquare,
      category: 'chat',
      gradient: 'from-blue-500 to-cyan-500',
      keywords: [
        'chat', 'ask', 'question', 'help', 'talk', 'converse', 'general',
        'how do i', 'what is', 'explain', 'advice', 'guidance', 'assistant'
      ],
      hint: 'Start a conversation to ask questions about patents, get analysis, or explore any topic.',
      action: () => {
        onStartChat?.();
        onOpenChange(false);
      }
    },
    {
      id: 'chat-patent',
      title: 'Analyze a Patent',
      description: 'Upload or paste a patent for detailed analysis',
      icon: Sparkles,
      category: 'chat',
      gradient: 'from-purple-500 to-pink-500',
      keywords: [
        'analyze', 'analysis', 'review', 'examine', 'evaluate', 'understand',
        'read patent', 'patent analysis', 'claim analysis', 'specification',
        'summary', 'summarize', 'abstract', 'what does this patent do'
      ],
      hint: 'Upload or paste a patent document and ask questions about claims, specifications, or prior art.',
      action: () => {
        onStartChat?.();
        onOpenChange(false);
        onOpenDocumentModal?.();
      }
    },

    // Document options
    {
      id: 'doc-view',
      title: 'View Document',
      description: 'Open and review patents, applications, or office actions',
      icon: FileText,
      category: 'document',
      gradient: 'from-green-500 to-emerald-500',
      keywords: [
        'view', 'open', 'read', 'document', 'patent', 'application',
        'look up', 'find patent', 'see patent', 'browse', 'viewer',
        'patent number', 'publication', 'full text'
      ],
      hint: 'Enter a patent or publication number to view the full document with claims, drawings, and prosecution history.',
      action: () => {
        onOpenDocumentModal?.();
        onOpenChange(false);
      }
    },
    {
      id: 'doc-upload',
      title: 'Upload Document',
      description: 'Upload PDFs, Word docs, or other files for analysis',
      icon: FileSearch,
      category: 'document',
      gradient: 'from-teal-500 to-green-500',
      keywords: [
        'upload', 'import', 'add file', 'pdf', 'word', 'docx', 'document',
        'attach', 'file', 'my document', 'local file', 'onboard',
        'load', 'bring in'
      ],
      hint: 'Upload a PDF, Word doc, or text file to analyze, summarize, or ask questions about its content.',
      action: () => {
        onOpenUploadModal?.();
        onOpenChange(false);
      }
    },

    // Task options
    {
      id: 'task-parts-list',
      title: 'Parts List',
      description: 'Generate a comprehensive parts list from patent drawings',
      icon: ListChecks,
      category: 'task',
      gradient: 'from-orange-500 to-red-500',
      keywords: [
        'parts', 'parts list', 'components', 'elements', 'reference numerals',
        'drawings', 'figures', 'numbering', 'bill of materials',
        'reference numbers', 'patent drawings', 'identify parts'
      ],
      hint: 'Automatically extract and number all components from patent drawings into a structured parts list.',
      action: () => {
        onNavigateToPage?.('tasks', undefined, 'parts_list');
        onOpenChange(false);
      }
    },
    {
      id: 'task-claim-chart',
      title: 'Claim Chart',
      description: 'Parse claims into phrases and identify supporting disclosure',
      icon: Table,
      category: 'task',
      gradient: 'from-indigo-500 to-blue-500',
      keywords: [
        'claim chart', 'claim mapping', 'claim support', 'claim limitation',
        'specification support', 'disclosure', 'claims support',
        'written description', 'enablement', 'claim element', 'mapping'
      ],
      hint: 'Break down each claim into individual phrases and map them to supporting disclosure in the specification.',
      action: () => {
        onNavigateToPage?.('tasks', undefined, 'claim_chart');
        onOpenChange(false);
      }
    },
    {
      id: 'task-office-action',
      title: 'Examine Office Action',
      description: 'Analyze and respond to office actions',
      icon: FileText,
      category: 'task',
      gradient: 'from-red-500 to-pink-500',
      keywords: [
        'office action', 'rejection', '103', '102', '101', '112',
        'examiner', 'response', 'amendment', 'argue', 'overcome',
        'final rejection', 'non-final', 'prior art', 'obviousness',
        'anticipation', 'prosecution', 'oa'
      ],
      hint: 'Upload or look up an office action to analyze rejections, view prior art, and prepare your response strategy.',
      action: () => {
        onNavigateToPage?.('examination');
        onOpenChange(false);
      }
    },
    {
      id: 'task-oa-reporting-email',
      title: 'OA Reporting Email',
      description: 'Generate professional email report for office action communication',
      icon: Mail,
      category: 'task',
      gradient: 'from-pink-500 to-rose-500',
      keywords: [
        'email', 'report', 'reporting', 'client', 'office action email',
        'communicate', 'notification', 'summary email', 'status update',
        'client report', 'attorney report', 'oa report'
      ],
      hint: 'Generate a professional email summarizing an office action for client or team communication.',
      action: () => {
        onNavigateToPage?.('tasks', undefined, 'oa_reporting_email');
        onOpenChange(false);
      }
    },
    {
      id: 'task-oa-shell',
      title: 'OA Response Shell',
      description: 'Generate a filled Office Action response shell from your firm\'s template',
      icon: FileOutput,
      category: 'task',
      gradient: 'from-rose-500 to-red-500',
      keywords: [
        'response shell', 'template', 'oa response', 'office action response',
        'fill template', 'response document', 'pre-filled', 'shell',
        'amendment', 'remarks', 'arguments', 'docx', 'word template', 'action'
      ],
      hint: 'Opens the Office action tool. Fetch the application, choose the Office action, read the claims, then generate the shell from your firm\'s template.',
      // Shells are generated in /action now: the shell is filled from the Office action
      // and the claims as read there, so the tool that holds both is where it belongs.
      action: () => {
        navigate('/action');
        onOpenChange(false);
      }
    },
    {
      id: 'task-translation',
      title: 'Translate Document',
      description: 'Translate patents or documents between languages',
      icon: Languages,
      category: 'task',
      gradient: 'from-violet-500 to-purple-500',
      keywords: [
        'translate', 'translation', 'language', 'japanese', 'chinese',
        'korean', 'german', 'french', 'spanish', 'foreign language',
        'convert language', 'multilingual', 'localize'
      ],
      hint: 'Translate patent documents, claims, or any text between supported languages while preserving technical terminology.',
      action: () => {
        onNavigateToPage?.('tasks', undefined, 'translate_document');
        onOpenChange(false);
      }
    },
    {
      id: 'task-ids',
      title: 'Generate IDS Form',
      description: 'Create Information Disclosure Statement forms for USPTO',
      icon: ClipboardList,
      category: 'ids',
      gradient: 'from-purple-500 to-indigo-500',
      keywords: [
        'ids', 'information disclosure', 'prior art references', 'cite',
        'duty of disclosure', 'sb/08', 'sb08', 'reference list',
        'submit references', 'download reference pdfs', 'patent citations',
        'reference pdfs', 'ids form', 'disclosure statement'
      ],
      hint: 'Enter patent numbers to auto-generate a complete IDS form. After generating, use "Reference PDFs" to download all cited patent documents.',
      action: () => {
        onNavigateToPage?.('ids');
        onOpenChange(false);
      }
    },
    {
      id: 'task-forms',
      title: 'USPTO Forms Generator',
      description: 'Generate pre-filled USPTO forms (RCE, TD, NOA, etc.) for multiple applications',
      icon: FileText,
      category: 'forms',
      gradient: 'from-indigo-500 to-purple-500',
      keywords: [
        'forms', 'rce', 'terminal disclaimer', 'notice of appeal',
        'transmittal', 'power of attorney', 'uspto forms', 'filing',
        'pre-filled', 'batch', 'multiple applications', 'td',
        'request for continued examination', 'poa'
      ],
      hint: 'Generate pre-filled USPTO forms (RCE, Terminal Disclaimer, Notice of Appeal, etc.) for one or more applications at once.',
      action: () => {
        onNavigateToPage?.('forms');
        onOpenChange(false);
      }
    },

    // Research options
    {
      id: 'research-uspto',
      title: 'USPTO Data & History',
      description: 'Search patents, view prosecution history, and track applications',
      icon: Search,
      category: 'research',
      gradient: 'from-amber-500 to-orange-500',
      keywords: [
        'search', 'look up', 'find', 'prosecution history', 'file wrapper',
        'patent search', 'application status', 'track', 'pair',
        'patent data', 'bibliography', 'citation', 'family',
        'continuity', 'status', 'pending', 'granted', 'abandoned'
      ],
      hint: 'Search for patents by number, view full prosecution history, track application status, and explore patent families.',
      action: () => {
        onNavigateToPage?.('uspto_data');
        onOpenChange(false);
      }
    },
    {
      id: 'research-mpep',
      title: 'MPEP & Resources',
      description: 'Access MPEP, CFR, USC, and other patent resources',
      icon: BookOpen,
      category: 'research',
      gradient: 'from-cyan-500 to-blue-500',
      keywords: [
        'mpep', 'cfr', 'usc', 'law', 'statute', 'regulation', 'rule',
        'manual', 'patent examination', 'procedure', 'legal',
        'title 35', 'title 37', 'reference', 'guidance', 'section'
      ],
      hint: 'Browse the MPEP, search CFR/USC provisions, and access patent law resources with AI-assisted navigation.',
      action: () => {
        onNavigateToPage?.('resources');
        onOpenChange(false);
      }
    },
  ];

  const isSearching = searchQuery.trim().length > 0;

  // Build searchable options from ALL tasks (merged with curated quickStartOptions)
  const allSearchableOptions = useMemo(() => {
    // Track task IDs already covered by curated quickStartOptions
    const coveredTaskIds = new Set(['parts_list', 'claim_chart', 'oa_reporting_email', 'translate_document']);

    const taskOptions: QuickStartOption[] = AVAILABLE_TASKS
      .filter(task => !task.hidden && !coveredTaskIds.has(task.id))
      .map(task => {
        const category = TASK_TO_CATEGORY[task.id] || 'legacy';
        const gradient = CATEGORY_GRADIENTS[category] || 'from-gray-500 to-slate-500';
        const Icon = task.icon || FileText;

        return {
          id: `task-${task.id}`,
          title: task.name,
          description: task.description,
          icon: Icon,
          category: 'task' as const,
          gradient,
          keywords: generateKeywords(task.name, task.description),
          hint: task.description,
          comingSoon: task.coming_soon,
          premiumOnly: task.premium_only,
          action: () => {
            onNavigateToPage?.('tasks', undefined, task.id);
            onOpenChange(false);
          },
        };
      });

    return [...quickStartOptions, ...taskOptions];
  }, [quickStartOptions, onNavigateToPage, onOpenChange]);

  /**
   * How the default view is laid out.
   *
   * Sixteen cards of equal weight, in one grid, said that translating a document and
   * drafting an application are the same size of decision. They are not: four of these
   * take a job end to end and are where most sessions should start, and the rest are
   * things you reach for inside one. Ordered by id so the grouping is explicit, and
   * anything not listed falls into "More" rather than disappearing.
   */
  const SECTIONS: { label: string; hint?: string; ids: string[]; feature?: boolean }[] = [
    {
      label: 'Take a job end to end',
      hint: 'Each opens its own workspace, with its own step-by-step guide inside it.',
      ids: ['drafting', 'action', 'fortech', 'docket'],
      feature: true,
    },
    {
      label: 'Read and ask',
      ids: ['chat-general', 'chat-patent', 'doc-view', 'doc-upload'],
    },
    {
      label: 'Produce a document',
      ids: [
        'task-ids',
        'task-forms',
        'task-oa-shell',
        'task-oa-reporting-email',
        'task-parts-list',
        'task-claim-chart',
        'task-translation',
      ],
    },
    {
      label: 'Look it up, or check it',
      ids: ['drafting-review', 'research-uspto', 'research-mpep', 'task-office-action'],
    },
  ];

  // Not memoised: it is a regroup of sixteen objects, and the list it reads is rebuilt on
  // every render anyway, so a memo over it would only add a dependency warning.
  const grouped = (() => {
    const byId = new Map(quickStartOptions.map(option => [option.id, option]));
    const used = new Set<string>();
    const sections = SECTIONS.map(section => {
      const items = section.ids
        .map(id => byId.get(id))
        .filter((option): option is QuickStartOption => Boolean(option));
      items.forEach(item => used.add(item.id));
      return { ...section, items };
    }).filter(section => section.items.length > 0);

    const rest = quickStartOptions.filter(option => !used.has(option.id));
    return rest.length > 0
      ? [...sections, { label: 'More', hint: undefined, ids: [], items: rest, feature: false }]
      : sections;
  })();

  const filteredOptions = useMemo(() => {
    if (isSearching) {
      const scored = allSearchableOptions
        .map(opt => ({ opt, score: scoreMatch(searchQuery, opt) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);
      return scored.map(({ opt }) => opt);
    }
    if (selectedCategory) {
      return quickStartOptions.filter(opt => opt.category === selectedCategory);
    }
    return quickStartOptions;
  }, [searchQuery, selectedCategory, allSearchableOptions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[700px] md:max-w-[900px] lg:max-w-[1100px] h-[85vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="px-6 pb-4 pt-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">What would you like to do?</h2>
            <p className="mt-0.5 text-sm text-gray-600">
              Pick a starting point, or describe the job and the right feature is found for
              you.
            </p>
          </div>

          {/* Search Input */}
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              autoFocus
              placeholder='Try "respond to an office action", "draft claims", "prior art in a field", "IDS"'
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim()) setSelectedCategory(null);
              }}
              onKeyDown={event => {
                // Escape clears the search rather than closing the dialog, while there is
                // something to clear: losing the whole screen to one keystroke is worse.
                if (event.key === 'Escape' && searchQuery) {
                  event.preventDefault();
                  event.stopPropagation();
                  setSearchQuery('');
                }
              }}
              className="h-10 pl-10 pr-8 text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category Filters */}
          {!isSearching && (
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button
                variant={selectedCategory === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(null)}
              >
                All
              </Button>
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <Button
                    key={cat.id}
                    variant={selectedCategory === cat.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat.id)}
                    className="flex items-center gap-2"
                  >
                    <Icon className="w-3 h-3" />
                    {cat.label}
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        {/* Options */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Searching or filtered: one flat grid, because the answer is a ranking and
              grouping a ranking throws the ranking away. */}
          {(isSearching || selectedCategory) && (
            <>
              {isSearching && (
                <p className="mb-3 text-xs text-gray-500">
                  {filteredOptions.length} match{filteredOptions.length === 1 ? '' : 'es'} for
                  {' '}“{searchQuery.trim()}”
                </p>
              )}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {filteredOptions.map(option => (
                  <OptionCard
                    key={option.id}
                    option={option}
                    body={isSearching ? option.hint : option.description}
                  />
                ))}
              </div>
            </>
          )}

          {/* The default: grouped, with the four workspaces given the room they deserve. */}
          {!isSearching && !selectedCategory && (
            <div className="space-y-6">
              {grouped.map(section => (
                <section key={section.label}>
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">{section.label}</h3>
                    {section.hint && (
                      <p className="text-xs text-gray-500">{section.hint}</p>
                    )}
                  </div>
                  <div
                    className={
                      section.feature
                        ? 'grid grid-cols-1 gap-3 md:grid-cols-2'
                        : 'grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3'
                    }
                  >
                    {section.items.map(option => (
                      <OptionCard
                        key={option.id}
                        option={option}
                        feature={section.feature}
                        body={section.feature ? option.hint : option.description}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {filteredOptions.length === 0 && isSearching && (
            <div className="text-center py-12 text-gray-500">
              <HelpCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No matching features found</p>
              <p className="text-sm mt-1">Try different keywords, or start a chat to ask any question</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => {
                  onStartChat?.();
                  onOpenChange(false);
                }}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Start a Chat Instead
              </Button>
            </div>
          )}

          {filteredOptions.length === 0 && !isSearching && (
            <div className="text-center py-12 text-gray-500">
              No options available for this category
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-gray-600">
              Everything here is reachable from the main menu at any time — this is only the
              short way in.
            </p>
            <div className="ml-auto flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  // The guide lives in the header; the same event the header listens for
                  // opens it, so this needs nothing threaded through.
                  window.dispatchEvent(new Event('open-user-guide'));
                }}
                className="flex items-center gap-2"
              >
                <BookOpen className="h-3 w-3" />
                Full user guide
              </Button>
              {onOpenWizard && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onOpenWizard();
                    onOpenChange(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Sparkles className="h-3 w-3" />
                  Guided tour
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One thing you can start from.
 *
 * `feature` is the larger treatment the four workspaces get: the same card, given its own
 * row on a wide screen and showing the fuller line rather than the one-liner, because
 * "answer an Office action" is not the same size of decision as "translate a document".
 */
function OptionCard({
  option,
  body,
  feature = false,
}: {
  option: QuickStartOption;
  body: string;
  feature?: boolean;
}) {
  const Icon = option.icon;
  return (
    <Card
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (
          event.target === event.currentTarget &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          option.action();
        }
      }}
      className={`group cursor-pointer border transition-all duration-200 hover:border-primary hover:shadow-md ${
        option.comingSoon ? 'opacity-70' : ''
      }`}
      onClick={option.action}
    >
      <CardHeader className={feature ? 'p-4' : 'p-3.5'}>
        <div className="flex items-start gap-3">
          <div
            className={`flex flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${
              option.gradient
            } ${feature ? 'h-10 w-10' : 'h-8 w-8'}`}
          >
            <Icon className={feature ? 'h-5 w-5 text-white' : 'h-4 w-4 text-white'} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <CardTitle className={feature ? 'text-base' : 'text-sm'}>
                {option.title}
              </CardTitle>
              <div className="ml-auto flex flex-shrink-0 items-center gap-1">
                {option.comingSoon && (
                  <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                    <Clock className="h-2.5 w-2.5" />
                    Soon
                  </span>
                )}
                {option.premiumOnly && (
                  <span className="inline-flex items-center gap-0.5 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                    <Crown className="h-2.5 w-2.5" />
                    Premium
                  </span>
                )}
                {feature ? (
                  <ArrowRight className="h-4 w-4 text-gray-300 transition-colors group-hover:text-primary" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-300 transition-colors group-hover:text-primary" />
                )}
              </div>
            </div>
            <CardDescription className={`mt-1 ${feature ? 'text-xs leading-relaxed' : 'text-xs'}`}>
              {body}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}
